import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Compass, Palette, Cpu, Rocket } from 'lucide-react';
import { NeuralNetwork } from '@/components/AnimatedGraphics';

const steps = [
  {
    id: '01',
    title: 'Scope & Synchronize',
    description:
      'Tell us your vision in plain English. Our AI architects instantly map your business requirements, scope the features, and define the technical roadmap.',
    icon: Compass,
    tag: '1\u20132 hours',
  },
  {
    id: '02',
    title: 'Vibe & Validate',
    description:
      'We generate high-fidelity, interactive designs in hours. You approve the exact look, feel, and user flow before a single line of logic is written.',
    icon: Palette,
    tag: 'Same day',
  },
  {
    id: '03',
    title: 'Generate & Elevate',
    description:
      'AI engines instantly construct the boilerplate code, freeing our senior MediaLabs developers to focus 100% on complex custom logic, API integrations, and enterprise security.',
    icon: Cpu,
    tag: 'Days',
  },
  {
    id: '04',
    title: 'Deploy & Dominate',
    description:
      'Launch your Version 1 fast to capture the market. You get complete source code ownership and a scalable, modern foundation to evolve your product.',
    icon: Rocket,
    tag: '48h\u201315 days',
  },
];

const StepCard = ({ step, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.6 });
  const Icon = step.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: index * 0.1 }}
      className="relative flex gap-5 sm:gap-6"
      data-testid="how-it-works-step"
      data-active={isInView ? 'true' : 'false'}
    >
      {/* Timeline rail + dot */}
      <div className="flex flex-col items-center">
        {/* Number dot */}
        <motion.div
          animate={{
            borderColor: isInView ? 'var(--brand-blue)' : 'hsl(214 32% 91%)',
            backgroundColor: isInView ? 'rgba(30, 172, 238, 0.08)' : 'transparent',
          }}
          transition={{ duration: 0.3 }}
          className="relative z-10 w-11 h-11 rounded-xl border-2 flex items-center justify-center shrink-0"
        >
          <motion.div
            animate={{ color: isInView ? 'var(--brand-blue)' : 'hsl(215 16% 40%)' }}
            transition={{ duration: 0.3 }}
          >
            <Icon className="w-5 h-5" strokeWidth={2} />
          </motion.div>
        </motion.div>

        {/* Vertical rail line */}
        {index < steps.length - 1 && (
          <div className="relative w-0.5 flex-1 min-h-[40px] bg-border my-2">
            <motion.div
              className="timeline-rail absolute top-0 left-0 w-full bg-brand-blue rounded-full"
              animate={{ scaleY: isInView ? 1 : 0 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
              style={{ height: '100%', transformOrigin: 'top' }}
            />
          </div>
        )}
      </div>

      {/* Content card */}
      <motion.div
        animate={{
          borderColor: isInView ? 'rgba(30, 172, 238, 0.3)' : 'var(--hairline)',
          boxShadow: isInView ? 'var(--shadow-soft)' : 'none',
        }}
        transition={{ duration: 0.3 }}
        className="flex-1 pb-10 sm:pb-12"
      >
        <div className="bg-white rounded-xl border p-5 sm:p-6 transition-colors duration-200 hover:shadow-[var(--shadow-soft)]">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-brand-blue tracking-wider">
                STEP {step.id}
              </span>
              <h3 className="text-base font-semibold text-foreground">
                {step.title}
              </h3>
            </div>
            <span className="shrink-0 inline-flex items-center text-xs font-medium text-brand-blue bg-brand-blue/8 border border-brand-blue/15 rounded-full px-2.5 py-1">
              {step.tag}
            </span>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {step.description}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      className="relative py-16 sm:py-20 lg:py-24 border-t border-[color:var(--hairline)]"
      data-testid="how-it-works-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          <div className="max-w-lg">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="text-xs font-semibold text-brand-blue uppercase tracking-wider">
                Our Process
              </span>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                How It Works
              </h2>
              <p className="mt-3 text-base text-muted-foreground leading-relaxed">
                From vision to launch in four precise steps. Every phase is engineered for speed without sacrificing quality.
              </p>
            </motion.div>

            {/* Neural Network illustration - visible on desktop */}
            <div className="hidden lg:block mt-8">
              <NeuralNetwork className="opacity-60" />
            </div>
          </div>

          {/* Timeline */}
          <div>
            {steps.map((step, index) => (
              <StepCard key={step.id} step={step} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
